import { Patient } from '../components/patients/PatientCard';

// Body Map Interfaces
export interface BodyRegion {
  id: number;
  code: string;
  name: string;
  description?: string;
  display_order: number;
  locations?: BodyLocation[];
}

export interface BodyLocation {
  id: number;
  code: string;
  name: string;
  display_name: string;
  region_id: number;
  region_name?: string;
  region_code?: string;
  laterality: 'none' | 'left' | 'right' | 'both';
  preferred_view: 'anterior' | 'posterior' | 'any';
  supports_laterality: boolean;
  anatomical_notes?: string;
  display_order?: number;
  is_active: boolean;
}

export interface PatientBodyFinding {
  id?: number;
  patient_id: number;
  appointment_id?: number;
  session_id?: number;
  body_location_id: number;
  body_location?: string;
  region?: string;
  laterality_specific?: 'left' | 'right' | 'bilateral' | 'central';
  finding_type: 'injury' | 'symptom' | 'examination' | 'procedure' | 'normal';
  finding_type_label?: string;
  severity?: 'mild' | 'moderate' | 'severe' | 'critical';
  severity_label?: string;
  severity_color?: string;
  description: string;
  clinical_notes?: string;
  pain_scale?: number;
  pain_description?: string;
  size_measurement?: string;
  color_description?: string;
  texture_description?: string;
  temperature?: 'normal' | 'warm' | 'hot' | 'cool' | 'cold';
  mobility?: 'normal' | 'limited' | 'fixed' | 'hypermobile';
  tenderness?: 'none' | 'mild' | 'moderate' | 'severe';
  swelling?: 'none' | 'mild' | 'moderate' | 'severe';
  status: 'active' | 'resolved' | 'improving' | 'worsening' | 'stable';
  status_label?: string;
  status_color?: string;
  recorded_by: number;
  reviewed_by?: number;
  recorded_date: string;
  follow_up_required: boolean;
  follow_up_date?: string;
  images_attached: boolean;
  images_count?: number;
  image_paths?: string[];
  recorded_by_name?: string;
  days_ago?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateBodyMapFindingData {
  patient_id: number;
  findings: Array<{
    body_location_id: number;
    laterality_specific?: 'left' | 'right' | 'bilateral' | 'central';
    finding_type: 'injury' | 'symptom' | 'examination' | 'procedure' | 'normal';
    severity?: 'mild' | 'moderate' | 'severe' | 'critical';
    description: string;
    clinical_notes?: string;
    pain_scale?: number;
    size_measurement?: string;
    color_description?: string;
    temperature?: 'normal' | 'warm' | 'hot' | 'cool' | 'cold';
    mobility?: 'normal' | 'limited' | 'fixed' | 'hypermobile';
    tenderness?: 'none' | 'mild' | 'moderate' | 'severe';
    swelling?: 'none' | 'mild' | 'moderate' | 'severe';
    follow_up_required?: boolean;
    follow_up_date?: string;
  }>;
}

export interface BodyMapFormOptions {
  finding_types: Record<string, string>;
  severities: Record<string, string>;
  statuses: Record<string, string>;
  laterality_specific: Record<string, string>;
  temperatures: Record<string, string>;
  mobility: Record<string, string>;
  tenderness: Record<string, string>;
  swelling: Record<string, string>;
  location_laterality: Record<string, string>;
  preferred_views: Record<string, string>;
}

export interface BodyMapStatistics {
  period_days: number;
  total_findings: number;
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
  by_status: Record<string, number>;
  follow_up: {
    requiring_follow_up: number;
    overdue_follow_up: number;
  };
  pain_levels: Record<string, number>;
}

export interface PatientBodyHistory {
  patient_id: number;
  patient_name: string;
  total_findings: number;
  findings_by_region: Record<string, {
    region_name: string;
    findings_count: number;
    findings: PatientBodyFinding[];
  }>;
  summary: {
    active_findings: number;
    critical_findings: number;
    requiring_follow_up: number;
    overdue_follow_up: number;
  };
}

class BodyMapService {
  private static BASE_URL = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api';

  private static async request(url: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('auth_token');
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(`${BodyMapService.BASE_URL}${url}`, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Don't log statistics errors to console to reduce noise
        if (!url.includes('/statistics')) {
          console.error('Body Map API Error:', errorData);
        }
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      // Don't log statistics errors to console to reduce noise
      if (!url.includes('/statistics')) {
        console.error('Body Map Request failed:', error);
      }
      throw error;
    }
  }

  // Get all body regions with their locations
  static async getBodyRegions(includeLocations = true): Promise<BodyRegion[]> {
    const result = await this.request(`/body-map/regions?include_locations=${includeLocations}`);
    return result.data;
  }

  // Get locations for a specific region
  static async getRegionLocations(regionId: number): Promise<{region: BodyRegion; locations: BodyLocation[]}> {
    const result = await this.request(`/body-map/regions/${regionId}/locations`);
    return result.data;
  }

  // Search body locations
  static async searchLocations(params: {
    search?: string;
    region_id?: number;
    laterality?: string;
    limit?: number;
  }): Promise<BodyLocation[]> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const url = `/body-map/locations/search${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const result = await this.request(url);
    return result.data;
  }

  // Get form options for dropdowns
  static async getFormOptions(): Promise<BodyMapFormOptions> {
    const result = await this.request('/body-map/form-options');
    return result.data;
  }

  // Save body map findings for a patient
  static async savePatientFindings(data: CreateBodyMapFindingData): Promise<{
    patient_id: number;
    findings_count: number;
    findings: PatientBodyFinding[];
  }> {
    try {
      const result = await this.request('/body-map/patients/' + data.patient_id + '/findings', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      
      if (result.success && result.data) {
        return result.data;
      } else if (result.data) {
        return result.data;
      } else {
        return result;
      }
    } catch (error) {
      console.error('Save patient findings error:', error);
      throw error;
    }
  }

  // Get body map findings for an appointment
  static async getAppointmentFindings(appointmentId: number): Promise<{
    appointment_id: number;
    patient_name: string;
    appointment_date: string;
    findings_count: number;
    findings: PatientBodyFinding[];
  }> {
    const result = await this.request(`/body-map/appointments/${appointmentId}/findings`);
    return result.data;
  }

  // Get patient's body map history
  static async getPatientBodyHistory(patientId: number, params?: {
    limit?: number;
    finding_type?: string;
    status?: string;
    days?: number;
  }): Promise<PatientBodyHistory> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const url = `/body-map/patients/${patientId}/history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const result = await this.request(url);
    return result.data;
  }

  // Update a body map finding
  static async updateFinding(findingId: number, data: Partial<{
    finding_type: string;
    severity: string;
    description: string;
    clinical_notes: string;
    pain_scale: number;
    status: string;
    follow_up_required: boolean;
    follow_up_date: string;
  }>): Promise<PatientBodyFinding> {
    const result = await this.request(`/body-map/findings/${findingId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return result.data;
  }

  // Delete a body map finding
  static async deleteFinding(findingId: number): Promise<void> {
    await this.request(`/body-map/findings/${findingId}`, {
      method: 'DELETE',
    });
  }

  // Get body map statistics (with fallback for backend errors)
  static async getStatistics(days = 30): Promise<BodyMapStatistics | null> {
    try {
      const result = await this.request(`/body-map/statistics?days=${days}`);
      return result.data;
    } catch (error) {
      // Return null for statistics errors to prevent UI breakage
      // This handles the backend typo: "requireingFollowUp" vs "requiringFollowUp"
      return null;
    }
  }

  // Helper methods for UI display
  static getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      'active': '#ef4444',
      'resolved': '#10b981',
      'improving': '#3b82f6',
      'worsening': '#dc2626',
      'stable': '#f59e0b',
    };
    return colors[status] || '#6b7280';
  }

  static getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      'active': 'Active',
      'resolved': 'Resolved',
      'improving': 'Improving',
      'worsening': 'Worsening',
      'stable': 'Stable',
    };
    return labels[status] || status;
  }

  static getStatusIcon(status: string): string {
    const icons: Record<string, string> = {
      'active': '🔴',
      'resolved': '✅',
      'improving': '📈',
      'worsening': '📉',
      'stable': '🟡',
    };
    return icons[status] || '⚪';
  }

  static getSeverityColor(severity: string): string {
    const colors: Record<string, string> = {
      'mild': '#10b981',
      'moderate': '#f59e0b',
      'severe': '#ef4444',
      'critical': '#dc2626',
    };
    return colors[severity] || '#6b7280';
  }

  static getSeverityLabel(severity: string): string {
    const labels: Record<string, string> = {
      'mild': 'Mild',
      'moderate': 'Moderate',
      'severe': 'Severe',
      'critical': 'Critical',
    };
    return labels[severity] || severity;
  }

  static getFindingTypeIcon(findingType: string): string {
    const icons: Record<string, string> = {
      'injury': '🤕',
      'symptom': '🩹',
      'examination': '📋',
      'procedure': '⚕️',
      'normal': '✅',
    };
    return icons[findingType] || '📋';
  }

  static getFindingTypeLabel(findingType: string): string {
    const labels: Record<string, string> = {
      'injury': 'Injury',
      'symptom': 'Symptom',
      'examination': 'Examination',
      'procedure': 'Procedure',
      'normal': 'Normal',
    };
    return labels[findingType] || findingType;
  }

  static getPainScaleColor(painScale: number): string {
    if (painScale <= 3) return '#10b981'; // Green
    if (painScale <= 6) return '#f59e0b'; // Yellow
    if (painScale <= 8) return '#ef4444'; // Red
    return '#dc2626'; // Dark red
  }

  static getPainScaleDescription(painScale: number): string {
    if (painScale <= 3) return 'Mild Pain';
    if (painScale <= 6) return 'Moderate Pain';
    if (painScale <= 8) return 'Severe Pain';
    return 'Very Severe Pain';
  }

  static formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  static formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}

export default BodyMapService;